import { Link, Outlet, useLocation } from "react-router-dom";
import JourneyAvatar from "@/components/JourneyAvatar";

const SUPPORT_EMAIL = "hello@journeywithmonte.com";

export default function PublicLayout() {
  const { pathname } = useLocation();
  const navLink = (to, label, testId) => {
    const active = pathname === to;
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`text-sm transition-colors ${
          active
            ? "text-white"
            : "text-white/55 hover:text-white"
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grain flex flex-col safe-x">
      <header className="relative z-10 border-b border-white/5 backdrop-blur-md bg-[#050505]/70 sticky top-0">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="public-home-link"
          >
            <JourneyAvatar size={32} ringed={false} testId="public-header-avatar" />
            <span className="font-display text-lg tracking-tight group-hover:text-cyan-300 transition-colors">
              Journey
            </span>
          </Link>

          <nav className="flex items-center gap-5 sm:gap-7">
            {navLink("/privacy", "Privacy", "public-nav-privacy")}
            {navLink("/terms", "Terms", "public-nav-terms")}
            {navLink("/messaging", "Messaging", "public-nav-messaging")}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="public-header-contact"
              className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/30 text-cyan-200 px-3.5 py-1.5 text-sm transition-colors"
            >
              Contact
            </a>
          </nav>
        </div>
      </header>

      <main className="relative z-10 flex-1">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-white/5 mt-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <JourneyAvatar size={24} ringed={false} testId="public-footer-avatar" />
              <span className="font-display text-base">Journey</span>
            </div>
            <p className="text-white/50 leading-relaxed">
              A thoughtful AI companion for memory, planning, and delegated follow-ups.
            </p>
          </div>

          <div>
            <div className="text-white/40 text-xs uppercase tracking-wider mb-3">
              Resources
            </div>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/privacy"
                  className="text-white/70 hover:text-white"
                  data-testid="public-footer-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-white/70 hover:text-white"
                  data-testid="public-footer-terms"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/messaging"
                  className="text-white/70 hover:text-white"
                  data-testid="public-footer-messaging"
                >
                  Messaging policy
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-white/40 text-xs uppercase tracking-wider mb-3">
              Contact
            </div>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="public-footer-email"
              className="text-cyan-300 hover:text-cyan-200 break-all"
            >
              {SUPPORT_EMAIL}
            </a>
            <p className="text-white/40 text-xs mt-2 leading-relaxed">
              For support, privacy inquiries, and opt-out requests.
            </p>
          </div>
        </div>

        <div className="border-t border-white/5">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-white/35">
            <span>© {new Date().getFullYear()} Journey. All rights reserved.</span>
            <span>journeywithmonte.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { SUPPORT_EMAIL };
